import React, { createContext, useContext } from 'react';
import { useQuery } from '@apollo/client';

import { COMPLETION_TYPES_QUERY } from '../graphql/queries';
import { useAuth } from './auth';
import { CompletionType } from '~/types';
import { SortedCompletionTypesProvider } from './sortedCompletionTypes';

interface CompletionTypesContextType {
  completionTypes: CompletionType[];
  loading: boolean;
}

const CompletionTypesContext = createContext<CompletionTypesContextType | undefined>(undefined);

const withAnalyticsName = (completionTypes: Omit<CompletionType, 'analyticsName'>[]): CompletionType[] => {
  return completionTypes.map((completionType) => {
    const analyticsName = completionType.name
      .toLowerCase()
      .replaceAll(' ', '_')
      .replaceAll('/', 'or')
      .replaceAll('(', '')
      .replaceAll(')', '');

    return {
      ...completionType,
      analyticsName,
    };
  });
};

export const CompletionTypesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { data, loading } = useQuery<{ completionTypes: Omit<CompletionType, 'analyticsName'>[] }>(
    COMPLETION_TYPES_QUERY,
    {
      skip: !user,
      fetchPolicy: 'cache-and-network',
    }
  );

  if (loading && !data) {
    return null;
  }

  const completionTypes = withAnalyticsName(data?.completionTypes || []);

  return (
    <CompletionTypesContext.Provider value={{ completionTypes, loading }}>
      <SortedCompletionTypesProvider completionTypes={completionTypes}>{children}</SortedCompletionTypesProvider>
    </CompletionTypesContext.Provider>
  );
};

export function useCompletionTypes() {
  const context = useContext(CompletionTypesContext);

  if (!context) throw new Error('useCompletionTypes must be used within an CompletionTypesProvider');

  return context;
}
